import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WorkOrderDetail } from '@/domain/work-order.types'
import { applyExternalEdit, type ExternalEdit } from '@/domain/work-order-external-edit'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { type WorkOrderDetailRead, workOrderApi } from '@/services/work-order.service'
import { WORK_ORDER_DETAIL_KEY, workOrderDetailKey } from './useWorkOrders'
import { invalidateWorkOrderActivity } from './useWorkOrderTimeline'

export type { ExternalEdit }

/** Root of the children key family: a child's cost edit invalidates every parent's table. */
const CHILDREN_KEY = 'work-order-children'

/** A parent's live children, for the Children table and the Costs panel. */
export const useWorkOrderChildren = (id: string | null, options?: { enabled?: boolean }) =>
	useQuery({
		queryKey: [CHILDREN_KEY, id],
		queryFn: () => workOrderApi.children(id as string),
		enabled: !!id && (options?.enabled ?? true),
	})

type QueryClient = ReturnType<typeof useQueryClient>

/** Patch the order in the detail aggregate and the single-order read; return what was there. */
function patchOrder(qc: QueryClient, id: string, edit: ExternalEdit) {
	const detail = qc.getQueryData<WorkOrderDetail>(workOrderDetailKey(id))
	const read = qc.getQueryData<WorkOrderDetailRead>(['work-order', id])
	if (detail) qc.setQueryData(workOrderDetailKey(id), applyExternalEdit(detail, edit))
	if (read) qc.setQueryData(['work-order', id], { ...read, workOrder: applyExternalEdit(read.workOrder, edit) })
	return { detail, read }
}

/**
 * Save a budget, a cost, the publishing date or the original codes from the work order page.
 * Optimistic: the new value shows in the same frame, and a failure puts back exactly what was
 * there before and says why in the status bar.
 *
 * Writes to one order are serialised by its scope, so a slow save's rollback cannot land after
 * a later save and revert it.
 */
export const useUpdateWorkOrderExternal = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		scope: { id: `work-order-update-${workOrderId}` },
		mutationFn: (edit: ExternalEdit) => workOrderApi.update(workOrderId, edit),
		onMutate: async (edit: ExternalEdit) => {
			await Promise.all([
				qc.cancelQueries({ queryKey: workOrderDetailKey(workOrderId) }),
				qc.cancelQueries({ queryKey: ['work-order', workOrderId] }),
			])
			return patchOrder(qc, workOrderId, edit)
		},
		onError: (error, _edit, snapshot) => {
			if (snapshot?.detail) qc.setQueryData(workOrderDetailKey(workOrderId), snapshot.detail)
			if (snapshot?.read) qc.setQueryData(['work-order', workOrderId], snapshot.read)
			notification.error(`Could not save: ${apiErrorMessage(error)}`)
		},
		onSuccess: () => notification.success('Saved.'),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: [WORK_ORDER_DETAIL_KEY, workOrderId] })
			void qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
			void qc.invalidateQueries({ queryKey: ['work-orders'] })
			// A child's cost moves its parent's totals.
			void qc.invalidateQueries({ queryKey: [CHILDREN_KEY] })
			invalidateWorkOrderActivity(qc)
		},
	})
}
